import { Box } from '@chakra-ui/react';
import { Group } from '@melio/penny';
import { useMelioIntl } from '@melio/platform-i18n';
import type { Plan, SubscriptionBillingCycleEnum } from '@melio/subscriptions-api';

import { usePlanInfo } from '../../../utils';
import { SubscribeToPlanType } from '../types';
import { PlanCardButton } from './PlanCardButton';
import { PlanCardFeaturesList } from './PlanCardFeaturesList';
import { PlanCardHeader } from './PlanCardHeader';
import { PlanCardPrice } from './PlanCardPrice';

type PlanCardProps = {
  plan: Plan;
  selectedBillingCycle: SubscriptionBillingCycleEnum;
  isFreePlan?: boolean;
  isSaving?: boolean;
  isCurrentPlan: boolean;
  isDisabled?: boolean;
  isSwitchingBillingCycle?: boolean;
  onSelectPlan: ({ planId, price }: SubscribeToPlanType) => void;
};
export const PlanCard = ({
  plan,
  selectedBillingCycle,
  isFreePlan,
  isSaving,
  isCurrentPlan,
  onSelectPlan,
  isDisabled,
  isSwitchingBillingCycle,
}: PlanCardProps) => {
  const { formatMessage } = useMelioIntl();
  const { id: planId, description, billingCycles } = plan;

  const billingCycle = billingCycles[selectedBillingCycle];
  const isMostPopular = false;
  const priceDescription = formatMessage(
    `activities.subscription.plans.billingTitle.${isFreePlan ? 'free' : 'monthly'}`
  );
  const { planName } = usePlanInfo({ planId });

  const onClick = () => {
    onSelectPlan({ planId, price: billingCycle?.price, billingCycle: selectedBillingCycle });
  };

  if (!billingCycle) {
    return null;
  }

  return (
    <Box minWidth="230px" flex={1} data-testid={`subscription-plan-card-${planId}`}>
      <Box
        padding="m"
        borderRadius="l"
        outline={isCurrentPlan ? '2px solid' : '1px solid'}
        outlineColor={isCurrentPlan ? 'brand.main' : 'neutral.light'}
        height="100%"
        backgroundColor={isMostPopular && !isCurrentPlan ? 'brand.lightest' : 'initial'}
      >
        <Group variant="vertical" spacing="m">
          <PlanCardHeader
            title={planName}
            description={description}
            isCurrentPlan={isCurrentPlan}
            isMostPopular={isMostPopular}
          />

          <PlanCardPrice price={billingCycle.price} priceHelperText={priceDescription} />

          <PlanCardButton
            planId={planId}
            isCurrent={isCurrentPlan}
            onClick={onClick}
            isSaving={isSaving}
            isDisabled={isDisabled}
            isFreePlan={isFreePlan}
            isSwitchingBillingCycle={isSwitchingBillingCycle}
            planName={planName}
          />

          <PlanCardFeaturesList isMostPopular={isMostPopular} planId={planId} />
        </Group>
      </Box>
    </Box>
  );
};
